import React, { useState, useEffect } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Plus, Edit2, Calendar as CalendarIcon, Gift, TrendingUp } from 'lucide-react';
import { format, differenceInMonths } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';

import { API_URL } from '../config/api';

export function StockOptions() {
  const { selectedEntity, isDutchEntity } = useEntity();
  const { t, language } = useLanguage();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);

  const [formData, setFormData] = useState({
    employee_name: '',
    employee_email: '',
    shares: '',
    strike_price: '',
    grant_date: new Date(),
    vesting_start: new Date(),
    vesting_months: '48',
    cliff_months: '12',
    notes: ''
  });

  useEffect(() => {
    if (selectedEntity) {
      fetchOptions();
    }
  }, [selectedEntity]);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/stock-options?entity_id=${selectedEntity.entity_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      }
    } catch (error) {
      console.error('Failed to fetch stock options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      entity_id: selectedEntity.entity_id,
      employee_name: formData.employee_name,
      employee_email: formData.employee_email || null,
      shares: parseInt(formData.shares),
      strike_price: parseFloat(formData.strike_price),
      currency: isDutchEntity ? 'EUR' : 'USD',
      grant_date: formData.grant_date.toISOString(),
      vesting_start: formData.vesting_start.toISOString(),
      vesting_months: parseInt(formData.vesting_months),
      cliff_months: parseInt(formData.cliff_months),
      notes: formData.notes || null
    };

    try {
      const url = editingOption
        ? `${API_URL}/api/stock-options/${editingOption.option_id}`
        : `${API_URL}/api/stock-options`;

      const response = await fetch(url, {
        method: editingOption ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        fetchOptions();
        resetForm();
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to save stock option:', error);
    }
  };

  const handleEdit = (opt) => {
    setEditingOption(opt);
    setFormData({
      employee_name: opt.employee_name,
      employee_email: opt.employee_email || '',
      shares: opt.shares.toString(),
      strike_price: opt.strike_price.toString(),
      grant_date: new Date(opt.grant_date),
      vesting_start: new Date(opt.vesting_start),
      vesting_months: opt.vesting_months.toString(),
      cliff_months: opt.cliff_months.toString(),
      notes: opt.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      employee_name: '',
      employee_email: '',
      shares: '',
      strike_price: '',
      grant_date: new Date(),
      vesting_start: new Date(),
      vesting_months: '48',
      cliff_months: '12',
      notes: ''
    });
    setEditingOption(null);
  };

  const calculateVestedShares = (option) => {
    const now = new Date();
    const vestingStart = new Date(option.vesting_start);
    const monthsElapsed = differenceInMonths(now, vestingStart);
    
    if (monthsElapsed < option.cliff_months) {
      return 0;
    }
    
    const vestedMonths = Math.min(monthsElapsed, option.vesting_months);
    return Math.floor((option.shares * vestedMonths) / option.vesting_months);
  };

  const calculateVestingProgress = (option) => {
    const now = new Date();
    const vestingStart = new Date(option.vesting_start);
    const monthsElapsed = differenceInMonths(now, vestingStart);
    
    if (monthsElapsed < option.cliff_months) {
      return (monthsElapsed / option.cliff_months) * 25; // Cliff progress (0-25%)
    }
    
    return Math.min(100, (monthsElapsed / option.vesting_months) * 100);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(isDutchEntity ? 'nl-NL' : 'en-US', {
      style: 'currency',
      currency: isDutchEntity ? 'EUR' : 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'exercised':
        return <Badge className="badge-success">{isDutchEntity && language === 'nl' ? 'Uitgeoefend' : 'Exercised'}</Badge>;
      case 'cancelled':
        return <Badge className="badge-error">{isDutchEntity && language === 'nl' ? 'Geannuleerd' : 'Cancelled'}</Badge>;
      case 'expired':
        return <Badge variant="outline">{isDutchEntity && language === 'nl' ? 'Verlopen' : 'Expired'}</Badge>;
      default:
        return <Badge className="badge-info">{isDutchEntity && language === 'nl' ? 'Actief' : 'Active'}</Badge>;
    }
  };

  const totalOptions = options.reduce((sum, opt) => sum + opt.shares, 0);
  const totalVested = options.reduce((sum, opt) => sum + calculateVestedShares(opt), 0);

  if (!selectedEntity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          {isDutchEntity ? 'Selecteer eerst een entiteit' : 'Select an entity first'}
        </p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('nav.stockOptions')}
          </h1>
          <p className="text-muted-foreground">{selectedEntity.name}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-brand" data-testid="add-option-btn">
              <Plus className="w-4 h-4 mr-2" />
              {isDutchEntity && language === 'nl' ? 'Optie Toevoegen' : 'Add Option'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingOption
                  ? (isDutchEntity && language === 'nl' ? 'Optie Bewerken' : 'Edit Option')
                  : (isDutchEntity && language === 'nl' ? 'Optie Toevoegen' : 'Add Option')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t('options.employee')}</Label>
                <Input
                  data-testid="opt-employee"
                  value={formData.employee_name}
                  onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="opt-email"
                  value={formData.employee_email}
                  onChange={(e) => setFormData({ ...formData, employee_email: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{isDutchEntity && language === 'nl' ? 'Aandelen' : 'Shares'}</Label>
                  <Input
                    type="number"
                    data-testid="opt-shares"
                    value={formData.shares}
                    onChange={(e) => setFormData({ ...formData, shares: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{t('options.strikePrice')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="opt-strike"
                    value={formData.strike_price}
                    onChange={(e) => setFormData({ ...formData, strike_price: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>{t('options.grantDate')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.grant_date, 'PPP', { locale: isDutchEntity && language === 'nl' ? nl : enUS })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.grant_date}
                      onSelect={(date) => date && setFormData({ ...formData, grant_date: date })}
                      locale={isDutchEntity && language === 'nl' ? nl : enUS}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label>{isDutchEntity && language === 'nl' ? 'Vesting Start' : 'Vesting Start'}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.vesting_start, 'PPP', { locale: isDutchEntity && language === 'nl' ? nl : enUS })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.vesting_start}
                      onSelect={(date) => date && setFormData({ ...formData, vesting_start: date })}
                      locale={isDutchEntity && language === 'nl' ? nl : enUS}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{isDutchEntity && language === 'nl' ? 'Vesting Periode (maanden)' : 'Vesting Period (months)'}</Label>
                  <Input
                    type="number"
                    data-testid="opt-vesting"
                    value={formData.vesting_months}
                    onChange={(e) => setFormData({ ...formData, vesting_months: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>{isDutchEntity && language === 'nl' ? 'Cliff (maanden)' : 'Cliff (months)'}</Label>
                  <Input
                    type="number"
                    data-testid="opt-cliff"
                    value={formData.cliff_months}
                    onChange={(e) => setFormData({ ...formData, cliff_months: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>{isDutchEntity && language === 'nl' ? 'Opmerkingen' : 'Notes'}</Label>
                <Textarea
                  data-testid="opt-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="flex-1 btn-brand" data-testid="save-option-btn">
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[hsl(var(--brand))]/10 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-[hsl(var(--brand))]" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {isDutchEntity && language === 'nl' ? 'Totaal Opties' : 'Total Options'}
                </p>
                <p className="text-2xl font-bold">{totalOptions.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {isDutchEntity && language === 'nl' ? 'Gevest' : 'Vested'}
                </p>
                <p className="text-2xl font-bold text-emerald-600">{totalVested.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {isDutchEntity && language === 'nl' ? 'Medewerkers' : 'Employees'}
                </p>
                <p className="text-2xl font-bold">{options.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Options List */}
      <Card>
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isDutchEntity && language === 'nl' ? 'Optie Grants' : 'Option Grants'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-2 border-[hsl(var(--brand))] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : options.length > 0 ? (
            <div className="space-y-4">
              {options.map(opt => {
                const vestedShares = calculateVestedShares(opt);
                const vestingProgress = calculateVestingProgress(opt);
                const isPastCliff = differenceInMonths(new Date(), new Date(opt.vesting_start)) >= opt.cliff_months;

                return (
                  <div 
                    key={opt.option_id}
                    className="border border-border rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{opt.employee_name}</h3>
                          {getStatusBadge(opt.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">{opt.employee_email}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(opt)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid sm:grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-muted-foreground">
                          {isDutchEntity && language === 'nl' ? 'Totaal' : 'Total'}
                        </p>
                        <p className="font-semibold">{opt.shares.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          {isDutchEntity && language === 'nl' ? 'Gevest' : 'Vested'}
                        </p>
                        <p className="font-semibold text-emerald-600">{vestedShares.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('options.strikePrice')}</p>
                        <p className="font-semibold">{formatCurrency(opt.strike_price)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('options.grantDate')}</p>
                        <p className="font-semibold">
                          {new Date(opt.grant_date).toLocaleDateString(isDutchEntity && language === 'nl' ? 'nl-NL' : 'en-US')}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">
                          {!isPastCliff 
                            ? (isDutchEntity && language === 'nl' ? 'Cliff periode' : 'Cliff period')
                            : (isDutchEntity && language === 'nl' ? 'Vesting voortgang' : 'Vesting progress')}
                        </span>
                        <span>{vestingProgress.toFixed(0)}%</span>
                      </div>
                      <Progress value={vestingProgress} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {opt.vesting_months} {isDutchEntity && language === 'nl' ? 'maanden vesting' : 'months vesting'}, 
                        {' '}{opt.cliff_months} {isDutchEntity && language === 'nl' ? 'maanden cliff' : 'months cliff'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center">
              <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {isDutchEntity && language === 'nl'
                  ? 'Nog geen opties. Voeg je eerste optie grant toe!'
                  : 'No stock options yet. Add your first option grant!'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
